import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Checkbox } from '@chakra-ui/react';
import { observer } from 'mobx-react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';

import { reqAddUserCompany, UserCompany } from '../../../models/user/UserCompany';
import { CompanyMaster } from '../../../models/company/Company';
import LoadingScreen from '../../../components/_helpers/LoadingScreen';
import { Input, Button, Label, ModalFooter, ModalBody } from '../../../common/materials';
import { withStore } from '../../../common/hocs';
import { WithStoreProps } from '../../../common/hocs/withStore';
import { useStore } from '../../../common/hooks';
import { ResponseList } from '../../../models';

type Props = {
    openModal: (isOpen: boolean) => void;
    userId: string;
};

const UserCompanyMappingComponent = observer((props: Props) => {
    const { userId, openModal } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [companyToken, setCompanyToken] = useState('');

    const [systemTypeOptions] = useState(['UAT', 'PRE-PRD', 'PRD']);
    const [selectedSystemTypes, setSelectedSystemTypes] = useState<string[]>([]);

    const { userCompanyStore } = useStore();
    const { currentUserCompanyMapping } = userCompanyStore;

    const existsUserCompany = currentUserCompanyMapping.filter(
        userCompany => userCompany.sid === companyToken
    );
    const tickedActiveSystemTypes = new Set(
        existsUserCompany.map(userCompany => userCompany.system_type)
    );

    const { data: pendingUserCompanies = [] } = useQuery({
        enabled: !!userId,
        queryKey: [userId, 'pending-user-companies'],
        queryFn: ({ signal }) =>
            axios.get<ResponseList<UserCompany>>('/v1/user/company', {
                signal,
                params: {
                    citizen_id: userId,
                    status: 'pending'
                }
            }),
        select: response => response.data.result_list
    });
    const existsPendingCompanySet = useMemo(
        () =>
            new Set(
                pendingUserCompanies
                    .filter(userCompany => userCompany.sid === companyToken)
                    .map(userCompany => userCompany.system_type)
            ),
        [pendingUserCompanies, companyToken]
    );

    useEffect(() => {
        if (existsPendingCompanySet.size > 0) {
            setSelectedSystemTypes(Array.from(existsPendingCompanySet));
        }
    }, [existsPendingCompanySet]);

    const handleCompanyTokenInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyToken(event.target.value);
    };

    const handleSystemTypeCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        setSelectedSystemTypes(prev => {
            if (checked) {
                return [...prev, value];
            } else {
                return prev.filter(option => option !== value);
            }
        });
    };

    const handleAddUserCompanyMappingClick = async () => {
        try {
            setIsLoading(true);

            if (!companyToken) {
                const result = await Swal.fire({
                    title: 'Error!',
                    text: 'SID not found in this company.',
                    icon: 'error',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Yes',
                    allowOutsideClick: false
                });

                if (result.isConfirmed) openModal(false);
                return;
            }

            const prepareSelectedSystemTypes = selectedSystemTypes.filter(
                systemType => !tickedActiveSystemTypes.has(systemType)
            );
            if (prepareSelectedSystemTypes.length === 0) {
                await Swal.fire({
                    title: 'Error!',
                    text: 'Please select at least one system type.',
                    icon: 'error',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Yes',
                    allowOutsideClick: false
                });

                return;
            }

            const reqBody: reqAddUserCompany = {
                citizen_id: userId,
                sid: companyToken,
                system_type: selectedSystemTypes
                    .filter(systemType => !tickedActiveSystemTypes.has(systemType))
                    .join('|')
            };

            const response = await fetch(`v2/user/company`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('SSO_AUTH'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reqBody)
            });

            const data: { message?: string } = await response.json();

            if (!response.ok) throw new Error(data.message);

            if (data.message && data.message === 'success') {
                const result = await Swal.fire({
                    title: 'Success!',
                    text: 'ส่งคำขอเข้าใช้งานเรียบร้อยแล้ว อยู่ระหว่างรอการอนุมัติจากผู้ดูแลระบบ',
                    icon: 'success',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Ok',
                    allowOutsideClick: false
                });

                if (result.isConfirmed) openModal(false);
            }
        } catch (error) {
            const result = await Swal.fire({
                title: 'Error!',
                text: (error as any).message,
                icon: 'error',
                showCancelButton: false,
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Yes',
                allowOutsideClick: false
            });

            if (result.isConfirmed) openModal(false);
        } finally {
            setIsLoading(false);
            setSelectedSystemTypes([]);
        }
    };

    return (
        <React.Fragment>
            <LoadingScreen loading={isLoading} />
            {/* temporary fix. should be refactored later. */}
            <ModalBody className="border">
                <span className="text-md font-semibold">Send Request</span>
                <div className="flex flex-col gap-1 p-2">
                    <Label>User</Label>
                    <Input
                        disabled
                        value={userId}
                        className="text-gray-500"
                    />

                    <Label className="mt-2">Company Token</Label>
                    <Input
                        placeholder={'Company Token'}
                        value={companyToken}
                        onChange={handleCompanyTokenInputChange}
                    />

                    <Label className="mt-2">System Types</Label>
                    <div className="flex gap-4">
                        {systemTypeOptions.map(option => {
                            const checked =
                                tickedActiveSystemTypes.has(option) ||
                                existsPendingCompanySet.has(option);
                            const isActive = checked && !existsPendingCompanySet.has(option);

                            !existsPendingCompanySet.has(option);

                            return (
                                <Checkbox
                                    key={option}
                                    isDisabled={checked}
                                    colorScheme="primary"
                                    value={option}
                                    isChecked={checked || selectedSystemTypes.includes(option)}
                                    onChange={!checked ? handleSystemTypeCheckboxChange : undefined}
                                    className={twMerge(
                                        isActive &&
                                            '[&_span[aria-hidden=true]]:border-success-700 [&_span[aria-hidden=true]]:bg-success-700 [&_svg]:stroke-white'
                                    )}
                                >
                                    {option}
                                </Checkbox>
                            );
                        })}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    size="md"
                    outline
                    className="text-gray-400"
                    onClick={() => openModal(false)}
                >
                    Cancel
                </Button>
                <Button
                    size="md"
                    className="cursor-pointer bg-primary-900 text-white disabled:opacity-60"
                    onClick={handleAddUserCompanyMappingClick}
                    disabled={companyToken === '' || selectedSystemTypes.length === 0}
                >
                    Save
                </Button>
            </ModalFooter>
        </React.Fragment>
    );
});

export default UserCompanyMappingComponent;
